import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2023 semantics GmbH
// Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
// Author      : Sebastian Bock
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 156393 $
// Purpose     : edit text ticker props
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Button, Menu } from 'semantic-ui-react';
// ---Framework imports
import { log } from 'uix/logging';
import { groupBy, values, size, range } from 'ldash';
import { Actions } from 'uix/registries';
import { Dialog, Toolbar, XMenu } from 'uix/components';
import { EditableLabel } from 'uix/components/editableLabel';
import { useActions, useApi, useEffect, useMemo, usePartContext, useResettableState, useState } from 'uix/hooks';
// ---Local imports
import { TextTicker } from './textTicker';
import { usePersistentOptions } from 'uix/modules/fulltext/hooks';
// ---Exports
// XXX: bad defaults - need to be read from parent ticker element
const DEFAULT_OPTS = {
    tickCharsPerSec: 13,
    digitSec: 0.1,
    sentenceEndSec: 0.1,
    commaSec: 0.1,
    initialWaitSec: 0,
    finishedWaitSec: 0,
    nextWaitSec: 0,
    scrollAlign: 'top',
    scrollFocusY: 15,
    height: '400px',
    lineBreak: false,
    debug: false
};
const deserializeDbFormat = (startIndex, stopIndex, words, pauses)=>{
    let dWords = [];
    let dPauses = {};
    startIndex = startIndex ? Number(startIndex) : 0;
    // log.x('checch', typeof pauses, pauses)
    if (typeof pauses === 'object' && !Object.keys(pauses)) {
        pauses = '';
    }
    if (words) {
        let Ranges = words.split(',');
        Ranges.forEach((R)=>{
            let startStop = R.split(':');
            for(let i = Number(startStop[0]); i <= Number(startStop[1]); i++){
                // if words is given, no stopIndex is given, so no need to check for that
                dWords.push(i + startIndex);
            }
        });
    } else if (stopIndex) {
        for(let i = startIndex; i <= Number(stopIndex); i++){
            dWords.push(i);
        }
    } else {
        // if only start Index is given, all words until end of ocr-text are selected
        // this will be computed at another place, where the information about text length is available
        // --> returning integer instead of list
        dWords = startIndex;
    }
    if (pauses) {
        let Ranges = pauses.split(',');
        Ranges.forEach((R)=>{
            let positionValue = R.split(':');
            dPauses[Number(positionValue[0]) + startIndex] = positionValue[1].toString();
        });
    }
    return {
        words: dWords,
        pauses: dPauses
    };
};
const serializeToDbFormat = (words, pauses)=>{
    let dbFormat = {};
    // log.x('stdf', words, pauses)
    const convertToTTSyntax = (idsArray)=>{
        let subListDict = {
            0: [
                idsArray[0]
            ]
        };
        let subListNumber = 0;
        for(let i = 1; i < idsArray.length; i++){
            if (idsArray[i] === idsArray[i - 1] + 1) {
                subListDict[subListNumber].push(idsArray[i]);
            } else {
                subListNumber++;
                subListDict[subListNumber] = [
                    idsArray[i]
                ];
            }
        }
        let ttSynDict = {
            startIndex: subListDict[0][0],
            stopIndex: null,
            words: null
        };
        if (Object.keys(subListDict).length === 1) {
            ttSynDict['stopIndex'] = subListDict[0][subListDict[0].length - 1];
        } else {
            ttSynDict['words'] = '';
            Object.values(subListDict).forEach((l)=>ttSynDict['words'] += `,${l[0] - idsArray[0]}:${l[l.length - 1] - idsArray[0]}`);
            ttSynDict['words'] = ttSynDict['words'].slice(1);
        }
        // convert to null, so that db will be overwritten
        // because JSON.stringify() ignores entries, where value is undefined
        if (typeof ttSynDict.startIndex === 'undefined') {
            ttSynDict.startIndex = null;
        }
        if (typeof ttSynDict.stopIndex === 'undefined') {
            ttSynDict.stopIndex = null;
        }
        return ttSynDict;
    };
    dbFormat = {
        ...convertToTTSyntax(words)
    };
    // log.x('gen dbformat', dbFormat)
    // calculate pauses relative to startIndex
    let newPauses = '';
    // if pauses -> change format
    if (Object.keys(pauses) && words.length) {
        // assuming that there are always words selected if pauses change
        // problem: construction might go wrong (e.g. selectedWordsIds[0] sth. strange
        // then the string will be empty, and dbFormat.pauses will not be updated
        newPauses = Object.entries(pauses).map((kvl)=>`${Number(kvl[0]) - words[0]}:${kvl[1]}`).join(',');
    }
    dbFormat = {
        ...dbFormat,
        pauses: newPauses
    };
    // log.x('gen dbformat final', dbFormat)
    return dbFormat;
};
const TextTickerPicker = ({ otid, otidPage, fulltextInfo, activateTicker, widgetMeta, reloadDbDataResult, setTickerStarted, scroll, setScroll, selectedWordsIds, setSelectedWordsIds, pauses, setPauses, saveAble })=>{
    let [opts, setOpts] = usePersistentOptions();
    let { i18n, reload } = usePartContext();
    let callApi = useActions(Actions.callApi);
    let [activeHoverIdx, setActiveHoverIdx] = useState(null);
    // log.x('ticker rendering', selectedWordsIds, pauses)
    // start and stop idx here are for internal selection handling, not related to idx that are
    // saved (loaded) to db or passed to TextTicker preview
    // internal selection handling is only based on words and pauses
    let [startIdx, setStartIdx] = useState(null);
    let [stopIdx, setStopIdx] = useState(null);
    let [pauseInsertMode, setPauseInsertMode] = useResettableState(false, [
        otid
    ]);
    useEffect(()=>{
        // delete pauses if they are not (anymore) in current selection
        // selectedWordsIds initially empty -> don't delete pauses then
        if (selectedWordsIds.length) {
            let newPauses = {};
            // log.x('pwpwpw', pauses, selectedWordsIds)
            newPauses = Object.fromEntries(Object.entries(pauses).filter(([idx])=>selectedWordsIds.includes(Number(idx))));
            // log.x('pauses changed bec of words changed', pauses, newPauses)
            setPauses(newPauses);
        }
    }, [
        selectedWordsIds
    ]);
    const handleSelection = (start, stop)=>{
        // log.x('hS', start, stop)
        // log.x('hS2', startIdx, stopIdx)
        if (start < 0) {
            return;
        }
        let startingInSelection = selectedWordsIds.includes(start);
        // log.x('sis', startingInSelection)
        // direction of selection important for optional handling of whitespace between words
        let backwardSelection = start > stop;
        // log.x('bs', backwardSelection)
        // ensure start <= stop, to standardize selection handling
        let cStart = start;
        start = Math.min(start, stop);
        stop = Math.max(cStart, stop);
        // log.x(start, stop)
        let idxList = Array.from({
            length: stop - start + 1
        }, (v, i)=>i + start);
        let newSelection = [];
        if (!startingInSelection) {
            newSelection = new Int32Array([
                ...new Set(selectedWordsIds.concat(idxList))
            ]).sort();
            setSelectedWordsIds(Array.from(newSelection));
        } else {
            newSelection = selectedWordsIds.filter((e)=>!idxList.includes(e));
            setSelectedWordsIds(newSelection);
        }
        setStartIdx(null);
        setStopIdx(null);
    // log.x('hs selectedWordsIds', newSelection)
    // log.x('hs', convertToTTSyntax(newSelection))
    // log.x('conv tts', convertToTTSyntax(newSelection))
    };
    // k ? `${k}="${v}"` : ''
    const menu = ()=>{
        return /*#__PURE__*/ _jsxs(Toolbar, {
            attached: "top",
            style: {
                position: 'sticky',
                backgroundColor: '#fff'
            },
            children: [
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    icon: "save",
                    title: "Auswahl und Pausen speichern",
                    disabled: !otid || !saveAble,
                    onClick: ()=>{
                        // log.x('saving', JSON.stringify(serializeToDbFormat(selectedWordsIds, pauses)))
                        callApi('presentation/saveTickerPage', {
                            otid,
                            data: JSON.stringify(serializeToDbFormat(selectedWordsIds, pauses))
                        });
                        reloadDbDataResult();
                    }
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    icon: "delete",
                    title: "Auswahl löschen",
                    onClick: ()=>{
                        setSelectedWordsIds([]);
                        setPauses({});
                    }
                }),
                /*#__PURE__*/ _jsx(XMenu.Toggle, {
                    icon: "pause",
                    title: "Pausen einfügen",
                    value: pauseInsertMode,
                    set: setPauseInsertMode
                }),
                /*#__PURE__*/ _jsx(XMenu.Item, {
                    icon: "play",
                    title: "Vorschau",
                    onClick: ()=>{
                        activateTicker(true), setTickerStarted(true);
                    }
                }),
                /*#__PURE__*/ _jsxs(Toolbar.Group, {
                    position: "right",
                    children: [
                        /*#__PURE__*/ _jsx(Menu.Item, {
                            children: /*#__PURE__*/ _jsx("input", {
                                style: {
                                    width: '72px'
                                },
                                type: "range",
                                min: 11,
                                max: 42,
                                step: 1,
                                value: opts.fontSize,
                                onChange: (event)=>setOpts({
                                        fontSize: event.target.value
                                    }),
                                title: `${i18n.get('btn_font_size_change_title')}`
                            })
                        }),
                        /*#__PURE__*/ _jsx(XMenu.Item, {
                            title: `${i18n.get('btn_font_size_reset_title')}`,
                            icon: [
                                'font',
                                'refresh'
                            ],
                            onClick: ()=>setOpts({
                                    fontSize: 18
                                })
                        })
                    ]
                })
            ]
        });
    };
    useEffect(()=>{
    // log.x('active', activeHoverIdx)
    // log.x('start', startIdx)
    // log.x('stop', stopIdx)
    }, [
        activeHoverIdx
    ]);
    const placedHolderStyle = {
        border: '1px dotted blue',
        marginRight: '2px',
        marginLeft: '2px',
        display: 'inline-block',
        height: `${opts.fontSize - 2}px`,
        width: `${opts.fontSize - 2}px`,
        position: 'relative',
        top: `${opts.fontSize / 10}px`
    };
    const pauseSpanStyle = {
        marginLeft: '1px'
    };
    const pauseTimeStyle = {
        verticalAlign: 'top',
        fontSize: `${2 / 3 * opts.fontSize}px`,
        position: 'relative',
        right: '2px'
    };
    const wrappingDivStyle = {
        fontSize: `${opts.fontSize}px`,
        lineHeight: 'normal',
        margin: '8px',
        overflow: 'auto'
    };
    const getHoverAwareText = (lines)=>{
        // let [activeHoverIdx, setActiveHoverIdx] = useState(null)
        // lead to changing number of hooks
        // log.x('ghat', selectedWordsIds)
        return /*#__PURE__*/ _jsxs("div", {
            style: {
                flex: 'auto'
            },
            onMouseUp: (e)=>{
                setStopIdx(activeHoverIdx);
                if (e.target.tagName === 'SPAN') {
                    // custom handling of selection using onHover(Index) of spans
                    window.getSelection().empty();
                    handleSelection(startIdx, activeHoverIdx);
                } else if (e.target.tagName === 'INPUT') {
                // input fields are (only) those for editing the time of pauses
                // here nothing should happen, because otherwise positioning the caret inside the input field using clicking
                // doesn't work
                } else if (window.getSelection().rangeCount) {
                    // if selection is ended outside of word-spans:
                    // get nodes in selection and append their ids to selectedWordsIds
                    // assuming only one selection, no multiselection
                    let nodeList = window.getSelection().getRangeAt(0).cloneContents();
                    let ids = Array.from(nodeList.children).map((c)=>Number(c.id));
                    let stop = startIdx === ids[0] ? ids[ids.length - 1] : ids[0];
                    handleSelection(startIdx, stop);
                    window.getSelection().empty();
                }
            // log.x('mouseup', startIdx, stopIdx)
            },
            children: [
                menu(),
                /*#__PURE__*/ _jsx("div", {
                    style: wrappingDivStyle,
                    onMouseDown: (e)=>{
                        // log.x('md', e, e.target, e.target.className)
                        // if (!e.target.className === 'pauseElement') {
                        //   setStartIdx(activeHoverIdx)
                        // }
                        // log.x('md', e, e.target, e.target.className)
                        // log.x(e.target.className === 'pauseElement')
                        if (e.target.className !== 'pauseElement') {
                            setStartIdx(activeHoverIdx);
                        } else {
                            setStartIdx(-1);
                        }
                    },
                    onMouseOver: (e)=>{
                        // log.x('div', e.target, e.target.tagName, typeof e.target.tagName)
                        if (e.target.tagName !== 'SPAN') {
                            // log.x('here')
                            setActiveHoverIdx(-1);
                        }
                    },
                    children: lines.map((line, lidx)=>line.map((word, widx)=>/*#__PURE__*/ _jsxs("span", {
                                id: word.index,
                                onMouseOver: ()=>{
                                    setActiveHoverIdx(word.index);
                                },
                                // className={word.index in selectedWordsIds ? styles.selectedWord : null}
                                style: {
                                    backgroundColor: selectedWordsIds.includes(word.index) ? '#FFBF4D' : null
                                },
                                children: [
                                    pauseInsertMode && selectedWordsIds.includes(word.index) || Object.keys(pauses).includes(word.index) ? word.text.replace(' ', '') // delete spaces in words, if pauses are placed next to them
                                     : widx === 0 && (lidx >= 1 ? lines[lidx - 1].slice(-1)[0].text !== '-' : false) ? ' ' + word.text // insert spaces if word is first in line, except previous line ended with '-'
                                     : word.text,
                                    Object.keys(pauses).includes(word.index.toString()) ? /*#__PURE__*/ _jsxs("span", {
                                        title: "Doppelklick zum Löschen",
                                        className: "pauseElement",
                                        onDoubleClick: ()=>{
                                            let { [word.index]: c, ...rest } = pauses;
                                            setPauses(rest);
                                        },
                                        children: [
                                            "⏸",
                                            /*#__PURE__*/ _jsx(EditableLabel, {
                                                labelClass: "pauseElement",
                                                text: pauses[word.index.toString()],
                                                style: pauseTimeStyle,
                                                selectText: true,
                                                onSave: (tv)=>{
                                                    let newPauses = {
                                                        ...pauses
                                                    };
                                                    newPauses[word.index] = tv;
                                                    setPauses(newPauses);
                                                }
                                            })
                                        ]
                                    }, 'P' + word.index) : null,
                                    pauseInsertMode && selectedWordsIds.includes(word.index) && !Object.keys(pauses).includes(word.index.toString()) ? /*#__PURE__*/ _jsx("span", {
                                        className: "pauseElement",
                                        style: placedHolderStyle,
                                        onClick: ()=>setPauses({
                                                ...pauses,
                                                [word.index]: '0.5'
                                            })
                                    }) : null
                                ]
                            }, word.index)))
                })
            ]
        });
    };
    let content = null;
    let lines = null;
    let raw_words = null || fulltextInfo.wordInfo?.words;
    if (raw_words) {
        let indexed_words = raw_words.map((w, index)=>[
                index,
                ...w
            ]);
        let line2words = groupBy(indexed_words, (w)=>w[6]);
        lines = values(line2words).map((line)=>line.map((w)=>({
                    index: w[0],
                    text: w[5],
                    bbox: w.slice(1, 5)
                })));
        content = getHoverAwareText(lines);
    }
    useEffect(()=>{
        // scroll once during initial rendering to first selected word
        // todo: what if selectedWordsIds is not yet loaded, when content changes. Because content
        // probably changes only once
        if (selectedWordsIds && selectedWordsIds.length && content && scroll) {
            document.getElementById(selectedWordsIds[0]).scrollIntoView({
                block: 'center',
                inline: 'nearest'
            });
            setScroll(false);
            // change initial fontSize. todo: how to do that correctly?
            setOpts({
                fontSize: 18
            });
        }
    }, [
        content
    ]);
    return content ? content : /*#__PURE__*/ _jsx("div", {});
};
export const TextTickerSelection = ({ otid })=>{
    //~ let _otid = 363550
    // let _otid = 612634
    //~ let [otid, setOtid] = useState(_otid)
    const ellipsis = ' ' + String.fromCodePoint(0x2026) + ' ';
    let [tickerStarted, setTickerStarted] = useState(false);
    let [tickerOpts, setTickerOpts] = useState({
        ...DEFAULT_OPTS,
        sequences: [
            {
                otid: otid
            }
        ]
    });
    let [activeTicker, setActiveTicker] = useState(false);
    let [dialogOpen, setDialogOpen] = useState(!otid);
    let [scroll, setScroll] = useState(true);
    let [selectedWordsIds, setSelectedWordsIds] = useState([]);
    // {id:length} with ids of words after which a pause should be placed
    let [pauses, setPauses] = useState({});
    let [saveAble, setSaveAble] = useState(false);
    let { data: dbData, reload: reloadDbDataResult } = useApi('presentation/loadTickerPage', {
        otid
    }, {
        autoreset: false,
        condition: otid
    });
    let { otidPage, widgetMeta = {} } = dbData || {};
    // fulltext data needed in hosting component, because deserializing need info about len(words) to
    // compute range(startIndex, documentEnd)
    let { data: data_fulltextInfo, reload: reloadFtDataResult } = useApi('getFulltextInfo', {
        otid: otidPage
    }, {
        condition: !!otidPage
    });
    let fulltextInfo = useMemo(()=>data_fulltextInfo?.fulltextInfo || {}, [
        data_fulltextInfo
    ]);
    // resets when otid changes
    useEffect(()=>{
        setActiveTicker(false);
        setScroll(true);
        reloadDbDataResult();
        reloadFtDataResult();
    }, [
        otid
    ]);
    useEffect(()=>{
        const picked = (({ startIndex, stopIndex, words, pauses })=>({
                startIndex,
                stopIndex,
                words,
                pauses
            }))(widgetMeta);
        setSaveAble(JSON.stringify(serializeToDbFormat(selectedWordsIds, pauses)) !== JSON.stringify(picked));
    }, [
        widgetMeta,
        selectedWordsIds,
        pauses
    ]);
    // deserialize db data to ticker data format and update local copy
    useEffect(()=>{
        // reset in case widgetMeta is empty
        setSelectedWordsIds([]);
        setPauses({});
        // log.x('w M', widgetMeta)
        if (widgetMeta && Object.keys(widgetMeta)) {
            // log.x('w M 2', widgetMeta)
            let { words: loadedWords, pauses: loadedPauses } = deserializeDbFormat(widgetMeta.startIndex, widgetMeta.stopIndex, widgetMeta.words, widgetMeta.pauses);
            if (loadedWords && typeof loadedWords === 'string' && fulltextInfo?.wordInfo?.words) {
                setSelectedWordsIds(range(Number(loadedWords), fulltextInfo?.wordInfo?.words.length + 1));
            } else if (loadedWords && Array.isArray(loadedWords)) {
                setSelectedWordsIds(loadedWords);
            }
            if (loadedPauses && Object.keys(loadedPauses)) {
                setPauses(loadedPauses);
            }
        // log.x('lw, lp', loadedWords, loadedPauses)
        }
    }, [
        widgetMeta,
        dbData
    ]);
    useEffect(()=>{
        setTickerOpts({
            ...tickerOpts,
            sequences: [
                {
                    otid: otidPage,
                    ...serializeToDbFormat(selectedWordsIds, pauses),
                    sep: ellipsis
                }
            ]
        });
    // log.x('tt opts', tickerOpts)
    }, [
        otidPage,
        selectedWordsIds,
        pauses
    ]);
    return !activeTicker ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Dialog, {
                open: dialogOpen,
                onSubmit: ()=>setDialogOpen(false),
                children: 'Dies hier ist kein Link. Daher ist Speichern nicht möglich.'
            }),
            /*#__PURE__*/ _jsx(TextTickerPicker, {
                otid: otid,
                otidPage: otidPage,
                fulltextInfo: fulltextInfo,
                activateTicker: setActiveTicker,
                reloadDbDataResult: reloadDbDataResult,
                setTickerStarted: setTickerStarted,
                scrol: scroll,
                setScroll: setScroll,
                selectedWordsIds: selectedWordsIds,
                setSelectedWordsIds: setSelectedWordsIds,
                pauses: pauses,
                setPauses: setPauses,
                saveAble: saveAble
            })
        ]
    }) : /*#__PURE__*/ _jsxs("div", {
        style: {
            display: 'block',
            height: '500px'
        },
        children: [
            /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsxs(XMenu, {
                    children: [
                        /*#__PURE__*/ _jsx(XMenu.Item, {
                            icon: "edit",
                            title: "Ticker bearbeiten",
                            onClick: ()=>setActiveTicker(false)
                        }),
                        tickerStarted ? /*#__PURE__*/ _jsx(XMenu.Item, {
                            icon: "stop",
                            title: "Ticker stoppen",
                            onClick: ()=>setTickerStarted(false)
                        }) : /*#__PURE__*/ _jsx(XMenu.Item, {
                            icon: "play",
                            title: "Ticker starten",
                            onClick: ()=>setTickerStarted(true)
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    outline: '1px solid #CCC',
                    height: '450px'
                },
                children: /*#__PURE__*/ _jsx(TextTicker, {
                    started: tickerStarted,
                    ...tickerOpts
                })
            })
        ]
    });
};
